import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

/**
 * Staff-facing privacy notice, reachable without signing in (it is linked from the login screen).
 *
 * The prose lives entirely in the `legal` i18n namespace as a `sections` array, so the three
 * locales stay line-for-line parallel and the page itself carries no copy. Sections may have
 * `paras`, `items`, or both — `items` render after `paras` where a section has the two.
 *
 * Deliberately NOT an acceptance gate: the lawful basis for processing here is contract +
 * legitimate interest, not consent, so an "I accept" checkbox would misstate the basis and
 * create a consent record we would then have to honour. The duty is to inform, and this informs.
 */

interface PrivacySection {
  heading: string;
  /** Lead-in prose, rendered above the list. */
  paras?: string[];
  items?: string[];
  /** Closing prose, rendered below the list — a caveat or qualifier on the items above it. */
  outro?: string[];
}

export function PrivacyNoticePage() {
  const { t } = useTranslation('legal');
  const { t: tc, i18n } = useTranslation('common');
  const { user } = useAuth();

  const isAuthenticated = user !== null;

  // `returnObjects` hands back the raw string key if the namespace failed to load, so guard the
  // shape rather than calling .map() on it. A legal page that white-screens is worse than one
  // that renders its header and nothing else.
  const raw = t('privacy.sections', { returnObjects: true });
  const sections: PrivacySection[] = Array.isArray(raw) ? (raw as PrivacySection[]) : [];

  return (
    <div className="min-h-dvh bg-slate-50 flex flex-col">
      {/* Header band — same brand gradient as the auth pages */}
      <header className="animated-gradient-bg text-white">
        <div className="mx-auto w-full max-w-3xl px-6 py-10 sm:py-14">
          <div className="flex items-start justify-between gap-6">
            <img src="/UnifiberLogoWhite.png" alt="Unifiber" className="h-8 sm:h-10 opacity-90" />

            {/* Language selector — the notice must be readable before sign-in, in any locale */}
            <div className="flex items-center gap-1 bg-white/[0.08] rounded-lg p-1 border border-white/[0.15]">
              {['EN', 'FR', 'NL'].map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => i18n.changeLanguage(l.toLowerCase())}
                  className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${
                    i18n.language.startsWith(l.toLowerCase())
                      ? 'bg-white/20 text-white'
                      : 'text-white/60 hover:text-white/90'
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          <h1 className="mt-8 text-3xl sm:text-4xl font-bold tracking-tight drop-shadow-sm">
            {t('privacy.title')}
          </h1>
          <p className="mt-2 text-white/75 text-sm sm:text-base">{t('privacy.subtitle')}</p>
          <p className="mt-4 text-white/50 text-xs">{t('privacy.lastUpdated')}</p>
        </div>
      </header>

      {/* Body */}
      <main className="flex-1">
        <div className="mx-auto w-full max-w-3xl px-6 py-10 sm:py-14">
          <Link
            to={isAuthenticated ? '/dashboard' : '/login'}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {isAuthenticated ? t('privacy.backToApp') : t('privacy.backToLogin')}
          </Link>

          <p className="mt-8 text-[15px] leading-relaxed text-slate-700">{t('privacy.intro')}</p>

          <div className="mt-10 space-y-10">
            {sections.map((section, i) => (
              <section key={i}>
                <h2 className="text-lg font-semibold text-slate-900 pb-2 border-b border-slate-200">
                  {section.heading}
                </h2>

                {section.paras?.map((para, j) => (
                  <p key={j} className="mt-4 text-[15px] leading-relaxed text-slate-700">
                    {para}
                  </p>
                ))}

                {section.items && (
                  <ul className="mt-4 space-y-2.5">
                    {section.items.map((item, j) => (
                      <li key={j} className="flex gap-3 text-[15px] leading-relaxed text-slate-700">
                        <span
                          aria-hidden="true"
                          className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#ed125f]"
                        />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {section.outro?.map((para, j) => (
                  <p key={j} className="mt-4 text-[15px] leading-relaxed text-slate-700">
                    {para}
                  </p>
                ))}
              </section>
            ))}
          </div>
        </div>
      </main>

      <footer className="border-t border-slate-200">
        <div className="mx-auto w-full max-w-3xl px-6 py-6 text-center">
          <p className="text-xs text-slate-400">
            {tc('footer.copyright', { year: new Date().getFullYear() })}
          </p>
        </div>
      </footer>
    </div>
  );
}
